import * as RadixDialog from '@radix-ui/react-dialog'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useT } from 'flyt/lib/language/language'
import { ChevronLeftIcon, XIcon } from 'flyt/ui/Icons/Icons'
import type { DialogProps, DialogSize } from './Dialog.types'

/**
 * dialog — controlled, feature-blind modal dialog surface.
 *
 * Open/close is driven entirely by the caller: every dismissal intent (Escape,
 * overlay click, close control) reports up through `onOpenChange` and the
 * dialog stays open until the caller flips `open`. While open it is a true
 * modal — focus trapped, outside pointer interaction disabled. ⚠️ That modality
 * is deliberate: an overlay that must stay clickable inside it (a popover)
 * re-asserts its own interactivity in THAT unit.
 * @radix-ui/react-dialog is confined here.
 *
 * TWO CONTROLS, TWO MEANINGS, THE SAME CORNERS EVERYWHERE. A CROSS at the head's
 * trailing edge always means leave; a BACK arrow at its leading edge, present
 * only on a dialog opened from another, always means one step back. They sit in
 * the head because it is the one band that never scrolls away. ⛔ The footer has
 * no way out at all: a footer control DOES something, and leaving is not.
 *
 * THE FOOTER IS THE DIALOG'S, NOT THE CALLER'S. Placement is a portal-wide rule
 * decided here once — acting controls at the trailing edge, strongest last. The
 * caller supplies controls and order, never placement, and no footer renders
 * when there are none. The body is likewise optional: with no body the two
 * dividing rules would enclose an empty band, so neither renders.
 *
 * THE BODY SCROLLS, NOT THE DIALOG. A centred fixed panel outgrowing the
 * viewport pushes its title above the top edge and its way out below the bottom,
 * neither reachable. So the panel is capped at the viewport, head and footer
 * hold their place, and the middle scrolls.
 */

const sizes: Record<DialogSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-2xl',
  xl: 'max-w-[72rem]',
  preview: 'max-w-[73.44rem]',
}

/**
 * The head's two icon controls share one appearance: a quiet square that only
 * shows itself under the pointer, sized to be hit on a touch screen. The glyph
 * never answers the pointer, so a press always lands on the button rather than
 * on a path inside it.
 */
const headControl =
  'flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-md text-text-muted outline-offset-2 hover:bg-text/8 hover:text-text focus-visible:outline-2 focus-visible:outline-current [&_svg]:pointer-events-none'

export function Dialog({
  open,
  dismissible = true,
  onOpenChange,
  title,
  titleAction,
  trigger,
  size = 'sm',
  children,
  onBack,
  actions,
}: DialogProps) {
  const t = useT('common')
  const hasBody = children !== undefined && children !== null && children !== false
  return (
    <RadixDialog.Root open={open} onOpenChange={(next) => { if (next || dismissible) onOpenChange(next) }}>
      {trigger !== undefined ? <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger> : null}
      <RadixDialog.Portal>
        {/* ABOVE THE APP'S OWN CHROME. The shell's navigation raises itself on
            the page, so a modal that only relies on paint order lands UNDER the
            sidebar the moment it is wide enough to reach it. A modal covers
            everything by definition; it says so here once, for every dialog. */}
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-text/40" />
        <RadixDialog.Content
          aria-describedby={undefined}
          className={`fixed top-1/2 left-1/2 z-50 flex max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border border-border bg-surface p-6 shadow-overlay ${sizes[size]}`}
        >
          {/* The head: back (only in a chain), the title, the way out. The two
              controls are pulled into the padding so their hover squares sit
              flush with the panel's corners while the title keeps its line. */}
          <div
            className={`flex shrink-0 flex-wrap items-center gap-2 ${
              hasBody ? 'mb-4 border-b border-border pb-3' : ''
            }`}
          >
            {onBack === undefined ? null : (
              <button
                type="button"
                onClick={onBack}
                aria-label={t('actions.back')}
                className={`-ml-2 ${headControl}`}
              >
                <ChevronLeftIcon width={20} height={20} />
              </button>
            )}
            <RadixDialog.Title className={`min-w-0 flex-1 text-lg font-semibold text-text [overflow-wrap:anywhere] ${titleAction === undefined ? '' : 'basis-full sm:basis-0'}`}>
              {title}
            </RadixDialog.Title>
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {titleAction}
              {dismissible && <button
                type="button"
                onClick={() => {
                  onOpenChange(false)
                }}
                aria-label={t('actions.close')}
                className={`-mr-2 ${headControl}`}
              >
                <XIcon width={20} height={20} />
              </button>}
            </div>
          </div>
          {hasBody ? <DialogBody>{children}</DialogBody> : null}
          {actions !== undefined ? (
            /* Everything in the footer acts, so the whole footer sits at the
               trailing edge where the eye finishes, and wraps within itself
               when it is too full for one line. */
            <div
              className={`mt-5 flex shrink-0 flex-wrap items-center justify-end gap-2 ${
                hasBody ? 'border-t border-border pt-4' : ''
              }`}
            >
              {actions}
            </div>
          ) : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}

/**
 * The one scrolling region of a dialog. `min-h-0` is what lets it actually give
 * way inside the flex column instead of pushing the footer out.
 *
 * ⚠️ THE HEADROOM IS NOT DECORATION. A box that scrolls vertically clips
 * horizontally too, and the focus rings the fields inside draw OUTSIDE their
 * own border box came out sliced flat at both edges. Four pixels of padding,
 * taken straight back out by the matching negative margin.
 *
 * ⚠️ THE SCROLLBAR IS THE PORTAL'S, AND ITS ROOM IS ALWAYS RESERVED. On a
 * machine set to show scroll bars always, the browser's own bar is a 15px grey
 * slab: every field narrowed by 15px the moment the body grew long enough to
 * scroll, in an OS grey that belongs to no surface here. A thin bar in the
 * portal's own border tone answers the tone, and a gutter held open on both
 * edges answers the reflow.
 *
 * CONTENT FADES AT THE CUT RATHER THAN STOPPING MID-STROKE. A scroll edge that
 * lands on a section card's 1px border leaves that border on screen with the
 * card gone. The mask dissolves the last 24px, and only on the side that HAS
 * more: a fade at an end the reader has already reached would say there is more
 * when there is not.
 */
function DialogBody({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null)
  const [cut, setCut] = useState({ above: false, below: false })

  const measure = useCallback(() => {
    const el = ref.current
    if (el === null) {
      return
    }
    // A pixel of slack: a scroll position is fractional on a zoomed display, so
    // an exact comparison reports "more below" at the very bottom.
    const above = el.scrollTop > 1
    const below = el.scrollTop + el.clientHeight < el.scrollHeight - 1
    setCut((prev) => (prev.above === above && prev.below === below ? prev : { above, below }))
  }, [])

  // ⚠️ WHAT IS BELOW CHANGES WITHOUT ANYBODY SCROLLING — a section opens, a
  // form step replaces another — so the children are watched, not just the box.
  useEffect(() => {
    const el = ref.current
    if (el === null) {
      return
    }
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    for (const child of el.children) {
      observer.observe(child)
    }
    measure()
    return () => {
      observer.disconnect()
    }
  }, [measure, children])

  return (
    <div
      ref={ref}
      onScroll={measure}
      className={`-mx-1 min-h-0 flex-1 overflow-y-auto px-1 [scrollbar-color:var(--color-border-strong)_transparent] [scrollbar-gutter:stable_both-edges] [scrollbar-width:thin] ${FADES[cut.above ? (cut.below ? 'both' : 'above') : cut.below ? 'below' : 'none']}`}
    >
      {children}
    </div>
  )
}

/**
 * The four readings of a scroll edge, written out rather than built, so every
 * one of them is a class Tailwind can see in the source.
 */
const FADES = {
  none: '',
  above: '[mask-image:linear-gradient(to_bottom,transparent,#000_24px)]',
  below: '[mask-image:linear-gradient(to_top,transparent,#000_24px)]',
  both: '[mask-image:linear-gradient(to_bottom,transparent,#000_24px,#000_calc(100%_-_24px),transparent)]',
}
